import { ApolloClient } from '@apollo/client';
import * as Sentry from '@sentry/core';
import { ComponentProps, forwardRef, useEffect, useState } from 'react';

import { AppContainer } from '@oui/app-core/src/components/AppContainer';
import { FLAGS } from '@oui/app-core/src/constants';
import { initApollo } from '@oui/app-core/src/lib/initApollo';
import { ThemeProvider } from '@oui/app-core/src/styles';

import FullLogo from '@src/assets/Full_logo.svg';
import { OnboardingGraphic } from '@src/components/OnboardingGraphic';
import { RootNavigator } from '@src/components/RootNavigator';
import { WidgetProviders } from '@src/components/WidgetProviders/WidgetProviders';
import { ptsdTheme } from '@src/theme';
import { DEEPLINK_CONFIG } from '@src/types/navigation';

const getMessages: ComponentProps<typeof AppContainer>['getMessages'] = ({ lang }) => {
  switch (lang) {
    case 'en': {
      return require('./messages/compiled/en.json');
    }
    default:
      return {};
  }
};

type Props = {};
export default forwardRef<AppContainer, Props>(function App({}: Props, ref) {
  const [apollo, setApollo] = useState<ApolloClient<unknown>>();

  useEffect(() => {
    // This will only run in development when fast-refresh kicks in
    let cleanupRef = { current: () => {} };

    initApollo((cb) => {
      cleanupRef.current = cb;
    })
      .then(setApollo)
      .catch(Sentry.captureException);

    return () => cleanupRef.current();
  }, []);

  return apollo ? (
    <ThemeProvider theme={ptsdTheme}>
      <AppContainer
        Logo={FullLogo}
        onboardingGraphic={<OnboardingGraphic />}
        flags={FLAGS}
        app={() => (
          <WidgetProviders>
            <RootNavigator />
          </WidgetProviders>
        )}
        apollo={apollo}
        ref={ref}
        initialPath={({ user }): Parameters<GeneratedClientRoutes.GetDeeplinkPath['ptsd']>[0] => {
          if (!user) return '/welcome';
          return '/learn';
        }}
        deeplinkConfig={{
          screens: DEEPLINK_CONFIG,
        }}
        getMessages={getMessages}
      />
    </ThemeProvider>
  ) : null;
});
